const createStyles = (styles: React.CSSProperties): React.CSSProperties => styles;

export const overlayStyles = createStyles({
	position: 'fixed',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	height: '100dvh',
	maxHeight: '100dvh',
	backgroundColor: 'rgba(0, 0, 0, 0.65)',
	backdropFilter: 'blur(10px)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	zIndex: 1050,
	animation: 'modalFadeIn 0.25s ease-out',
	padding: '16px',
	paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
	overflowY: 'auto',
});

export const containerStyles = createStyles({
	backgroundColor: 'var(--color-bg-elevated)',
	borderRadius: 'var(--radius-xl, 12px)',
	boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)',
	width: '100%',
	maxWidth: '520px',
	maxHeight: 'calc(100dvh - 32px)',
	overflowY: 'auto',
	display: 'flex',
	flexDirection: 'column',
	animation: 'modalScaleIn 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
});

export const headerStyles = createStyles({
	padding: '32px 28px 0',
	textAlign: 'center',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: '8px',
});

export const headerIconStyles = createStyles({
	width: '52px',
	height: '52px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: '14px',
	background: 'linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-secondary) 100%)',
	marginBottom: '4px',
	boxShadow: '0 4px 16px rgba(30, 86, 49, 0.3)',
});

export const titleStyles = createStyles({
	margin: 0,
	fontFamily: 'var(--font-family-display)',
	fontSize: 'var(--type-xl)',
	color: 'var(--color-text-primary)',
	letterSpacing: '-0.01em',
	lineHeight: 'var(--leading-tight)',
});

export const subtitleStyles = createStyles({
	margin: 0,
	fontFamily: 'var(--font-family-regular)',
	fontSize: 'var(--type-sm)',
	color: 'var(--color-text-secondary)',
	lineHeight: 'var(--leading-normal)',
});

export const progressSectionStyles = createStyles({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	padding: '24px 28px 8px',
	gap: '8px',
});

export const progressCountStyles = createStyles({
	fontFamily: 'var(--font-family-regular)',
	fontSize: 'var(--type-sm)',
	color: 'var(--color-text-tertiary)',
	letterSpacing: 'var(--tracking-wide)',
});

export const checklistSectionStyles = createStyles({
	display: 'flex',
	flexDirection: 'column',
	gap: '10px',
	padding: '16px 28px',
});

export const getItemStyles = (isChecked: boolean): React.CSSProperties => ({
	display: 'flex',
	alignItems: 'center',
	gap: '14px',
	padding: '14px 16px',
	borderRadius: 'var(--radius-lg, 8px)',
	border: `1.5px solid ${isChecked ? 'var(--color-success)' : 'var(--color-border-default)'}`,
	backgroundColor: isChecked ? 'rgba(34, 197, 94, 0.04)' : 'var(--color-bg-primary)',
	cursor: 'pointer',
	transition: 'all 200ms cubic-bezier(0.22, 1, 0.36, 1)',
	userSelect: 'none',
});

export const itemIconContainerStyles = (isChecked: boolean): React.CSSProperties => ({
	width: '40px',
	height: '40px',
	minWidth: '40px',
	borderRadius: '10px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	background: isChecked
		? 'linear-gradient(135deg, var(--color-success) 0%, #16a34a 100%)'
		: 'linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-secondary) 100%)',
	transition: 'background 200ms ease',
	flexShrink: 0,
});

export const itemContentStyles = createStyles({
	flex: 1,
	minWidth: 0,
});

export const itemTitleStyles = createStyles({
	margin: 0,
	fontFamily: 'var(--font-family-semibold)',
	fontSize: 'var(--type-sm)',
	color: 'var(--color-text-primary)',
	lineHeight: 'var(--leading-tight)',
	marginBottom: '3px',
});

export const itemDescriptionStyles = createStyles({
	margin: 0,
	fontFamily: 'var(--font-family-regular)',
	fontSize: '12px',
	color: 'var(--color-text-tertiary)',
	lineHeight: 'var(--leading-normal)',
});

export const checkCircleStyles = (isChecked: boolean): React.CSSProperties => ({
	width: '28px',
	height: '28px',
	minWidth: '28px',
	borderRadius: '50%',
	border: isChecked ? 'none' : '2px solid var(--color-border-default)',
	backgroundColor: isChecked ? 'var(--color-success)' : 'transparent',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	transition: 'all 200ms cubic-bezier(0.22, 1, 0.36, 1)',
	animation: isChecked ? 'checkPop 300ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
	flexShrink: 0,
});

export const footerStyles = createStyles({
	padding: '12px 28px 28px',
});

export const getButtonStyles = (allChecked: boolean): React.CSSProperties => ({
	width: '100%',
	height: '50px',
	border: 'none',
	borderRadius: 'var(--radius-lg, 8px)',
	fontFamily: 'var(--font-family-semibold)',
	fontSize: 'var(--type-sm)',
	letterSpacing: 'var(--tracking-wide)',
	cursor: allChecked ? 'pointer' : 'not-allowed',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '10px',
	transition: 'all 250ms cubic-bezier(0.22, 1, 0.36, 1)',
	backgroundColor: allChecked ? 'var(--color-brand-secondary)' : 'var(--color-bg-tertiary)',
	color: allChecked ? '#ffffff' : 'var(--color-text-disabled)',
	boxShadow: allChecked ? '0 4px 16px rgba(40, 100, 57, 0.35)' : 'none',
	transform: allChecked ? 'translateY(0)' : 'none',
});
